import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom';
import axios from 'axios';
import PublicPhotoGrid from './components/PublicPhotoGrid';
import PublicPhotoViewer from './components/PublicPhotoViewer';
import AlbumsGrid from './components/AlbumsGrid';
import AboutPage from './components/AboutPage';
import './App.css';

const API_BASE = process.env.REACT_APP_PUBLIC_API_URL || 'http://localhost:3002/api/public';

function applyTheme(theme) {
    const root = document.documentElement;
    if (!theme) {
        root.removeAttribute('data-themed');
        root.removeAttribute('data-card-style');
        root.style.removeProperty('--theme-primary');
        root.style.removeProperty('--theme-secondary');
        root.style.removeProperty('--theme-accent');
        root.style.removeProperty('--theme-bg');
        root.style.removeProperty('--theme-text');
        root.style.removeProperty('--theme-card-bg');
        root.style.removeProperty('--theme-heading-font');
        root.style.removeProperty('--theme-body-font');
        root.style.removeProperty('--theme-card-style');
        return;
    }
    root.setAttribute('data-themed', 'true');
    root.setAttribute('data-card-style', theme.card_style || 'minimal');
    root.style.setProperty('--theme-primary', theme.primary_color);
    root.style.setProperty('--theme-secondary', theme.secondary_color);
    root.style.setProperty('--theme-accent', theme.accent_color);
    root.style.setProperty('--theme-bg', theme.background_color);
    root.style.setProperty('--theme-text', theme.text_color);
    root.style.setProperty('--theme-card-bg', theme.card_background_color || '#1a1a1a');
    root.style.setProperty('--theme-heading-font', `'${theme.heading_font}', serif`);
    root.style.setProperty('--theme-body-font', `'${theme.body_font}', sans-serif`);
    root.style.setProperty('--theme-card-style', theme.card_style);

    // Load Google Fonts dynamically
    const fonts = [theme.heading_font, theme.body_font].filter(Boolean);
    fonts.forEach(font => {
        const fontId = `gfont-${font.replace(/\s+/g, '-').toLowerCase()}`;
        if (!document.getElementById(fontId)) {
            const link = document.createElement('link');
            link.id = fontId;
            link.rel = 'stylesheet';
            link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(font)}:wght@300;400;500;600&display=swap`;
            document.head.appendChild(link);
        }
    });
}

function TenantGallery() {
    const { tenantName, albumName } = useParams();
    const [tenant, setTenant] = useState(null);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [albumPhotos, setAlbumPhotos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchTenant();
        fetchTheme();
    }, [tenantName]);

    const fetchTenant = async () => {
        try {
            const response = await axios.get(`${API_BASE}/${tenantName}/info`);
            setTenant(response.data);
            setError('');
        } catch (error) {
            console.error('Error fetching tenant:', error);
            setError('Gallery not found');
        } finally {
            setLoading(false);
        }
    };

    const fetchTheme = async () => {
        try {
            const response = await axios.get(`${API_BASE}/${tenantName}/theme`);
            applyTheme(response.data);
        } catch (error) {
            console.error('Error fetching theme:', error);
        }
    };

    const handlePhotoClick = useCallback((photo, photos) => {
        setSelectedPhoto(photo);
        setAlbumPhotos(photos);
    }, []);

    const handleCloseViewer = useCallback(() => {
        setSelectedPhoto(null);
    }, []);

    if (loading) {
        return (
            <div className="loading">
                <p>Loading gallery...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-page">
                <h1>Gallery Not Found</h1>
                <p>{error}</p>
            </div>
        );
    }

    // If no album is specified, show the albums grid
    if (!albumName) {
        return (
            <div className="App">
                <AlbumsGrid
                    tenantName={tenantName}
                    tenant={tenant}
                />
            </div>
        );
    }

    // If "about" is specified, show the about page
    if (albumName === 'about') {
        return (
            <div className="App">
                <AboutPage
                    tenantName={tenantName}
                    tenant={tenant}
                />
            </div>
        );
    }

    // If an album is specified, show the photo grid for that album
    return (
        <div className="App">
            <PublicPhotoGrid
                tenantName={tenantName}
                albumName={albumName}
                tenant={tenant}
                onPhotoClick={handlePhotoClick}
            />
            {selectedPhoto && (
                <PublicPhotoViewer
                    photo={selectedPhoto}
                    photos={albumPhotos}
                    tenantName={tenantName}
                    tenant={tenant}
                    albumName={albumName}
                    onClose={handleCloseViewer}
                    onNavigate={setSelectedPhoto}
                />
            )}
        </div>
    );
}

function App() {
    return (
        <Router>
            <Routes>
                <Route path="/:tenantName/:albumName" element={<TenantGallery />} />
                <Route path="/:tenantName" element={<TenantGallery />} />
                <Route path="/" element={
                    <div className="home-page">
                        <h1>Photo Gallery</h1>
                        <p>Please specify a gallery name in the URL</p>
                        <p>Example: /tenant-a or /tenant-b/album-name</p>
                    </div>
                } />
            </Routes>
        </Router>
    );
}

export default App;
